import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, shape, extend
} from '@thd-nucleus/data-sources';
import {
  Button,
  Drawer,
  DrawerHeader
} from '@one-thd/sui-atomic-components';
import { Add } from '@one-thd/sui-icons';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { useStore } from '@thd-nucleus/experience-context';
import { ADD_TO_TYPE, ANALYTICS_SELECT_PROJECT, SCREENS, TRACK } from './AddToConstants';
import { PreviouslyPurchased } from './common/previously-purchased/PreviouslyPurchased';
import { AddToContext } from './AddToContext';
import { SelectGroup } from './addto/project/SelectGroup';
import { DefaultScreen } from './common/DefaultScreen';
import {
  projectsModel,
  projectDetailsModel,
  createGroupModel,
} from './hooks/useProjects';
import { listDetailsModel, updateListModel, createListModel } from './hooks/useLists';
import { AddToProject } from './addto/project/AddToProject';
import { AddToList } from './addto/list/AddToList';
import { CopyProject } from './addto/copyProject/CopyProject';
import { AddToSelection } from './common/addto-selection/AddToSelection';
import { SaveToFavoriteIconButton } from './addto/list/SaveToFavoriteIconButton';
import { useBenefitCheck } from './hooks/useBenefitCheck';
import {
  triggerAnalytics, updateNonQuoteableProducts, handleOpenATQ, triggerLoadingDrawerAnalytics
} from './AddToHelpers';
import { ProductExceptions } from './common/product-exceptions/ProductExceptions';
import { LoadingStatus } from './common/products-loading-status/LoadingStatus';
import { useSelectedItems } from './hooks/useSelectedItems';

export const AddTo = (props) => {
  const {
    project,
    list,
    addToType: addToTypeFromProp,
    addToFallbackType,
    products,
    copyProjectData,
    storeId: storeIdFromProp,
    CustomAddToActionElement,
    showSelectionScreen,
    onSuccessfulAddToGroup,
    showBackButton,
    projectDetails,
    loadedProductsData,
    updateLoadedProductsData
  } = props;
  const defaultAddToType = addToTypeFromProp === ADD_TO_TYPE.PROJECT && addToFallbackType !== null
    ? addToFallbackType : addToTypeFromProp;
  const [addToType, setAddToType] = useState(defaultAddToType || null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [pageSize, setPageSize] = useState(30);
  const store = useStore();
  const { storeId: projectStoreId, zipCode: projectZipCode, projectId, projectName } = projectDetails || {};
  const storeId = storeIdFromProp || projectStoreId || store?.storeId;
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState(
    { itemId: null, quantity: null, productData: null }
  );
  const addToQuoteRef = useRef(null);
  const quoteTimeTrackingRef = useRef({ startTime: null });

  const { isProjectsEligible } = useBenefitCheck({
    skip: !(addToTypeFromProp === ADD_TO_TYPE.PROJECT || showSelectionScreen)
  });

  useEffect(() => {
    if (addToTypeFromProp === ADD_TO_TYPE.PROJECT) {
      if (!isProjectsEligible) {
        setAddToType(addToFallbackType);
      } else {
        setAddToType(addToTypeFromProp);
      }
    }
  }, [isProjectsEligible]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to.ready');
  }, []);

  const {
    PROJECT_SELECT_GROUP, DEFAULT, MINI_PIP_BASE, PROJECT_SELECT_PROJECT, ADD_TO_SELECTION,
    LOADING_STATUS, PRODUCT_EXCEPTIONS
  } = SCREENS;

  const loadingProgress = useMemo(() => {
    if (addToType !== ADD_TO_TYPE.QUOTE && !showSelectionScreen) {
      return null;
    }

    const totalProducts = products?.length || 0;
    if (totalProducts === 0) return 0;

    const matchCount = products.filter((product) => {
      const productItemId = product?.itemId;
      const productId = product?.id;

      return loadedProductsData.some((obj) => (productItemId && obj?.itemId === productItemId)
      || (productId && obj?.key === productId)
      );
    }).length;

    return Math.round((matchCount / totalProducts) * 100);
  }, [loadedProductsData, products, addToType, showSelectionScreen]);

  const isLoading = loadingProgress !== 100;

  const {
    selectedCatalogItems,
    selectedQuotableCatalogItemDetails,
    selectedCatalogItemDetails,
    selectedQCItemDetails,
    selectedNonSharedQCItemDetails
  } = useSelectedItems({ products, loadedProductsData, loadingProgress, showSelectionScreen });

  const handleClose = () => {
    const currentScreen = screen;
    setInitialLoad(true);
    setScreen(null);
    if (showSelectionScreen) {
      setAddToType(null);
    }
    setShowErrorMessage(false);
    setShowSuccessMessage(false);
    setSelectedGroup(null);
    setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    setOpen(false);
    if (currentScreen === LOADING_STATUS && quoteTimeTrackingRef.current.startTime) {
      triggerLoadingDrawerAnalytics('loading interrupted', quoteTimeTrackingRef.current.startTime);
    }
    quoteTimeTrackingRef.current.startTime = null;
  };

  const nonQuoteableProducts = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return updateNonQuoteableProducts(
      selectedCatalogItemDetails,
      selectedQCItemDetails,
      selectedNonSharedQCItemDetails
    );
  }, [loadingProgress, selectedCatalogItemDetails, selectedQCItemDetails, selectedNonSharedQCItemDetails]);

  useEffect(() => {
    if (addToType === ADD_TO_TYPE.QUOTE && open) {
      quoteTimeTrackingRef.current.startTime = Math.floor(Date.now() / 1000);
    }
  }, [addToType, open]);

  useEffect(() => {
    if (!open || addToType !== ADD_TO_TYPE.QUOTE) return;

    if (isLoading) {
      if (screen !== LOADING_STATUS) {
        setScreen(LOADING_STATUS);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.loadingScreen');
      }
      return;
    }

    triggerLoadingDrawerAnalytics('loading finished', quoteTimeTrackingRef.current.startTime);

    if (nonQuoteableProducts?.length > 0 && loadingProgress === 100) {
      setScreen(PRODUCT_EXCEPTIONS);
    } else {
      handleOpenATQ(addToQuoteRef);
      handleClose();
    }
  }, [open, loadingProgress, addToType, nonQuoteableProducts]);

  const handleOpen = () => {
    if (showSelectionScreen) {
      setScreen(ADD_TO_SELECTION);
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addTo');
    }
    if (addToType === 'Project') {
      if (products?.length > 0) {
        setScreen(PROJECT_SELECT_PROJECT);
        triggerAnalytics(ANALYTICS_SELECT_PROJECT, {});
      } else {
        setScreen(DEFAULT);
      }
    }
    setOpen(true);
    if (addToType === 'Project') {
      if (products?.length === 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('projects: add items overlay');
      }
    }
    if (window.digitalData?.page?.pageInfo?.pageType === 'pip') {
      window.LIFE_CYCLE_EVENT_BUS.trigger(TRACK.BUTTON_CLICK, {});
    }
    if (addToType === 'List') {
      if (products?.length > 0) {
        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.OPEN_DRAWER, {});
      }
    }
  };

  const handleBackPress = () => {
    if (screen === PROJECT_SELECT_GROUP || screen === MINI_PIP_BASE) {
      setScreen(DEFAULT);
      setSelectedGroup(null);
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    }
    if (screen.startsWith('MINI_PIP')) {
      setScreen(MINI_PIP_BASE);
    }
  };

  const context = {
    projectRefetch: () => { },
    handleBackPress,
    handleClose,
    screen,
    setScreen,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    addToType,
    products,
    selectedGroup,
    setSelectedGroup,
    storeId
  };

  if (addToType === null && !showSelectionScreen) {
    return null;
  }
  const showCustomButton = CustomAddToActionElement && addToType === addToTypeFromProp;
  const { showIconButton = false } = list;
  return (
    <>
      {
        showSelectionScreen && (
          <Button
            startIcon={Add}
            variant="tertiary"
            onClick={handleOpen}
            disabled={products?.length === 0 && copyProjectData?.items?.length === 0}
          >
            Add to
          </Button>
        )
      }
      {
        showIconButton && (
          <SaveToFavoriteIconButton
            storeId={storeId}
            products={products}
            handleOpen={handleOpen}
          />
        )
      }
      {
        showCustomButton && (
          <CustomAddToActionElement onClick={handleOpen} />
        )
      }
      {
        !showCustomButton && !showIconButton && !showSelectionScreen && (
          <Button
            fullWidth
            variant="secondary"
            onClick={handleOpen}
            aria-label={`Add to ${addToType}`}
            data-testid={`add-to-${addToType}`}
          >
            Add to {addToType === 'CopyProject' ? 'Project' : addToType}
          </Button>
        )
      }
      <AddToContext.Provider value={context}>
        <Drawer open={open} onClose={handleClose}>
          <>
            {screen === ADD_TO_SELECTION && (
              <AddToSelection
                products={products}
                setAddToType={setAddToType}
                handleClose={handleClose}
                isProjectsEligible={isProjectsEligible}
                copyProjectData={copyProjectData?.items}
              />
            )}
            {
              (addToType === 'Project') && (
                <AddToProject
                  products={products}
                  projectId={project?.projectId}
                  onSuccessGroupClick={project?.onSuccessGroupClick}
                  expandSelectedGroup={project?.expandSelectedGroup}
                  initialLoad={initialLoad}
                  setInitialLoad={setInitialLoad}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  onSuccessfulAddToGroup={onSuccessfulAddToGroup}
                  showBackButton={showBackButton}
                />
              )
            }
            {
              (addToType === 'List') && (
                <AddToList
                  products={selectedCatalogItems ?? products}
                  showCount={list?.showCount}
                  skipListId={list?.skipListId}
                  isSharedList={list?.isSharedList}
                  listId={list?.listId}
                  onMaterialsAdded={list?.onMaterialsAdded}
                />
              )
            }
            {
              (addToType === 'CopyProject') && (
                <CopyProject
                  copyProjectData={copyProjectData}
                />
              )
            }
            {
              (addToType === ADD_TO_TYPE.QUOTE) && (
                <>
                  <DrawerHeader
                    title={`Add to ${addToType}`}
                    onClose={handleClose}
                  />
                  {screen === LOADING_STATUS && (
                    <LoadingStatus
                      products={products}
                      loadingProgress={loadingProgress}
                      projectDetails={projectDetails}
                      loadedProductsData={loadedProductsData}
                      updateLoadedProductsData={updateLoadedProductsData}
                    />
                  )}
                  {screen === PRODUCT_EXCEPTIONS && (
                    <ProductExceptions
                      productExceptions={nonQuoteableProducts}
                      handleOpenATQ={() => handleOpenATQ(addToQuoteRef)}
                      showATQ={selectedQuotableCatalogItemDetails?.length > 0}
                    />
                  )}
                </>
              )
            }
          </>
        </Drawer>
      </AddToContext.Provider>
      {(addToType === ADD_TO_TYPE.QUOTE || showSelectionScreen) && (
        <div className="sui-hidden">
          <AddToQuote
            buttonRef={addToQuoteRef}
            products={selectedQuotableCatalogItemDetails}
            productExceptions={nonQuoteableProducts}
            projectQuoteData={{
              projectZipCode,
              projectName,
              projectId
            }}
            quoteStartTime={quoteTimeTrackingRef.current.startTime}
          />
        </div>
      )}
    </>
  );
};

AddTo.displayName = 'AddTo';

AddTo.propTypes = {
  addToType: PropTypes.oneOf(
    [ADD_TO_TYPE.PROJECT, ADD_TO_TYPE.LIST, ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.CART, ADD_TO_TYPE.COPY_PROJECT, null]
  ),
  addToFallbackType: PropTypes.oneOf(
    [ADD_TO_TYPE.PROJECT, ADD_TO_TYPE.LIST, ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.CART, null]
  ),
  products: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number,
      itemType: PropTypes.string,
      id: PropTypes.string,
      sku: PropTypes.string,
      bridgeSolution: PropTypes.shape({
        optionKey: PropTypes.string,
        qcId: PropTypes.string
      }),
    })),
    PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number
    }))
  ]),
  copyProjectData: PropTypes.shape({
    sourceList: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.string),
    targetProjectId: PropTypes.string
  }),
  storeId: PropTypes.string,
  CustomAddToActionElement: PropTypes.func,
  showSelectionScreen: PropTypes.bool,
  project: PropTypes.shape({
    projectId: PropTypes.string,
    onSuccessGroupClick: PropTypes.func,
    expandSelectedGroup: PropTypes.func,
  }),
  list: PropTypes.shape({
    showCount: PropTypes.bool,
    isSharedList: PropTypes.bool,
    showIconButton: PropTypes.bool,
    skipListId: PropTypes.string,
    listId: PropTypes.string,
    onMaterialsAdded: PropTypes.func
  }),
  onSuccessfulAddToGroup: PropTypes.func,
  showBackButton: PropTypes.bool,
  projectDetails: PropTypes.shape({}),
  loadedProductsData: PropTypes.arrayOf(PropTypes.shape({})),
  updateLoadedProductsData: PropTypes.func
};

AddTo.defaultProps = {
  project: {
    projectId: null,
    onSuccessGroupClick: () => { },
    expandSelectedGroup: () => { },
  },
  list: {
    skipListId: null,
    showIconButton: false,
    showCount: false,
    isSharedList: false,
    listId: null,
    onMaterialsAdded: () => { }
  },
  products: [],
  copyProjectData: {},
  storeId: null,
  CustomAddToActionElement: null,
  showSelectionScreen: false,
  addToType: null,
  addToFallbackType: null,
  onSuccessfulAddToGroup: () => { },
  showBackButton: true,
  projectDetails: {},
  loadedProductsData: [],
  updateLoadedProductsData: () => {}
};

AddTo.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      brandName: string(),
      productLabel: string(),
      storeSkuNumber: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
},
useBenefitCheck,
AddToProject,
projectsModel,
DefaultScreen,
PreviouslyPurchased,
SelectGroup,
AddToList,
SaveToFavoriteIconButton,
// CreateGroup,
projectDetailsModel,
createGroupModel,
listDetailsModel,
updateListModel,
createListModel,
CopyProject,
LoadingStatus,
AddToQuote
);